import * as React from 'react'
import type { ComponentPropsWithoutRef, JSX } from 'react'

import { clsx } from 'clsx'

import {
  Flex,
  Text,
  ChannelIcon as PopcornChannelIcon,
  UnstyledButton,
  Tooltip,
  VisuallyHidden,
} from '@buffer-mono/popcorn'
import type { ChannelIconProps as PopcornChannelIconProps } from '@buffer-mono/popcorn'

import type { Service } from '../../graphqlEnums'
import {
  getChannelColor,
  normalizeNetworkName,
} from '~publish/helpers/channels'

import styles from './CollapsedChannelSection.module.css'

// Client-side validation issues are warnings; server-side save failures are critical.
export type ChannelIssueSeverity = 'warning' | 'critical'

export type ChannelIssue = {
  message: string
  severity?: ChannelIssueSeverity
}

// Highest priority first: a save failure (critical) outranks a validation warning.
const SEVERITY_PRIORITY: readonly ChannelIssueSeverity[] = [
  'critical',
  'warning',
]

const getHighestSeverity = (
  issues: ChannelIssue[],
): ChannelIssueSeverity | undefined =>
  SEVERITY_PRIORITY.find((severity) =>
    issues.some((issue) => (issue.severity ?? 'critical') === severity),
  )

const renderIssueTooltip = (issues: ChannelIssue[]): React.ReactNode => {
  // Dedupe by message so identical messages render once and give stable,
  // collision-free keys without falling back to the array index.
  const messages = [...new Set(issues.map((issue) => issue.message))]
  return messages.length === 1 ? (
    messages[0]
  ) : (
    <>
      {messages.map((message) => (
        <p key={message}>{`• ${message}`}</p>
      ))}
    </>
  )
}

// The Root publishes the row's highest severity; ChannelIcon consumes it to
// render the validation chip, so the indicator stays anchored to the icon
// without the consumer wiring it up or splicing the children array.
const SeverityContext = React.createContext<ChannelIssueSeverity | undefined>(
  undefined,
)

type CollapsedChannelSectionRootProps = ComponentPropsWithoutRef<
  typeof UnstyledButton
> & {
  issues?: ChannelIssue[]
}

const CollapsedChannelSectionRoot = React.forwardRef<
  HTMLButtonElement,
  CollapsedChannelSectionRootProps
>(({ className, children, issues = [], ...props }, ref) => {
  const severity = getHighestSeverity(issues)
  const hasIssues = severity !== undefined

  const button = (
    <UnstyledButton
      ref={ref}
      className={clsx(styles.sectionButton, className)}
      aria-invalid={hasIssues ? true : undefined}
      {...props}
    >
      {children}
    </UnstyledButton>
  )

  return (
    <SeverityContext.Provider value={severity}>
      <div className={styles.collapsedChannelSection} data-severity={severity}>
        {hasIssues ? (
          <Tooltip content={renderIssueTooltip(issues)}>{button}</Tooltip>
        ) : (
          button
        )}
      </div>
    </SeverityContext.Provider>
  )
})

CollapsedChannelSectionRoot.displayName = 'CollapsedChannelSection.Root'

const ChannelIcon = ({
  className,
  service,
  channelCount,
  ...props
}: Omit<PopcornChannelIconProps, 'type'> & {
  service: Service
  channelCount?: number
}): JSX.Element => {
  const channelColor = getChannelColor(service)
  const severity = React.useContext(SeverityContext)
  const style: React.CSSProperties & Record<string, string> = {
    '--channel-color': channelColor,
  }
  return (
    <Flex
      align="center"
      justify="center"
      className={styles.channelIconContainer}
      style={style}
      data-channel-count={
        channelCount && channelCount > 1 ? channelCount : undefined
      }
    >
      <span className={styles.iconSurface}>
        <PopcornChannelIcon
          className={className}
          type={service}
          size="small"
          {...props}
        />
      </span>
      <VisuallyHidden>{normalizeNetworkName(service)}</VisuallyHidden>
      {severity ? (
        <span
          className={styles.validationChip}
          data-severity={severity}
          aria-hidden
        />
      ) : null}
    </Flex>
  )
}

ChannelIcon.displayName = 'CollapsedChannelSection.ChannelIcon'

const TextPreview = ({
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<typeof Text>): JSX.Element => {
  return (
    <Text
      className={clsx(styles.textPreview, className)}
      as="p"
      color="subtle"
      truncate
      {...props}
    >
      {children}
    </Text>
  )
}

TextPreview.displayName = 'CollapsedChannelSection.TextPreview'

const AttachmentPreview = ({
  className,
  ...props
}: {
  className?: string
  children: React.ReactNode
}): JSX.Element => {
  return (
    <Flex
      align="center"
      justify="center"
      className={clsx(styles.attachmentPreview, className)}
      {...props}
    />
  )
}

AttachmentPreview.displayName = 'CollapsedChannelSection.AttachmentPreview'

export const CollapsedChannelSection = Object.assign(
  CollapsedChannelSectionRoot,
  {
    ChannelIcon,
    TextPreview,
    AttachmentPreview,
  },
)
